import Script from "next/script";
import { type CSSProperties, useEffect, useRef } from "react";

import Text from "@/blueprint/components/Text/Text";
import type { ShopPayInstallmentsTerms } from "@/lib/shopPayInstallments";
import { cn } from "@/lib/utils";

const PAYMENT_TERMS_LOADER =
  "https://cdn.shopify.com/shopifycloud/shop-js/modules/v2/loader.payment-terms.en.esm.js";

/**
 * Shopify's own Shop Pay Installments line ("From $X/mo at 0% APR with Shop")
 * and its plan pop-up, which carries the lending disclosures. Its copy and
 * figures are Shopify's; only the type and colour are ours, inherited
 * through its shadow root. The background variable picks the Shop logo's
 * light or dark variant, so it names the surface the line sits on.
 */
const SURFACE_COLORS = {
  page: "var(--color-surface-page)",
  white: "var(--color-brand-white)",
} as const;

const ShopPayInstallments = ({
  terms,
  className,
  surface = "page",
  hideAmount = false,
  variant = "paragraph-sm",
}: {
  terms: ShopPayInstallmentsTerms;
  className?: string;
  /** The surface under the line, so Shopify picks its logo's variant. */
  surface?: keyof typeof SURFACE_COLORS;
  /**
   * Drops Shopify's "From $X/mo" where the price beside it already says it
   * (/product's sticky bar), leaving "or 0% APR with shop" and its pop-up.
   */
  hideAmount?: boolean;
  /** The line's size; /product's title sets it a step up. */
  variant?: "paragraph-sm" | "paragraph-md";
}) => {
  const termsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!hideAmount) return;
    const host = termsRef.current;
    if (host === null) return;
    let observer: MutationObserver | null = null;
    let cancelled = false;
    customElements
      .whenDefined("shopify-payment-terms")
      .then(() => {
        const root = host.shadowRoot;
        if (cancelled || root === null) return;
        trimAmount(root);
        observer = new MutationObserver(() => trimAmount(root));
        observer.observe(root, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      })
      .catch((error: unknown) => {
        console.error("Shop Pay Installments line failed to load", error);
      });
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [hideAmount, terms.meta]);

  return (
    <>
      <Script src={PAYMENT_TERMS_LOADER} type="module" strategy="lazyOnload" />
      <Text
        variant={variant}
        as="div"
        tone="secondary"
        className={cn("min-h-[1lh]", className)}
        style={
          {
            "--payment-terms-background-color": SURFACE_COLORS[surface],
          } as CSSProperties
        }
      >
        <shopify-payment-terms
          ref={termsRef}
          variant-id={terms.variantId}
          shopify-meta={terms.meta}
        />
      </Text>
    </>
  );
};

/**
 * Shopify renders its line as plain text and a bold amount in one span
 * ("From", "$X", "/mo or 0% APR with", logo), with no part to style, so the
 * amount is trimmed from the rendered text: everything up to and including
 * "/mo". Idempotent, as Shopify may render again; a markup change it doesn't
 * recognise leaves the full line.
 */
const trimAmount = (root: ShadowRoot) => {
  const content = root.getElementById("shopify-installments-content");
  if (content === null) return;
  const nodes = [...content.childNodes];
  const perMonth = nodes.findIndex(
    (node) =>
      node.nodeType === Node.TEXT_NODE &&
      (node.textContent ?? "").includes("/mo"),
  );
  if (perMonth === -1) return;
  for (const node of nodes.slice(0, perMonth)) node.remove();
  const rest = nodes[perMonth];
  if (rest !== undefined)
    rest.textContent = (rest.textContent ?? "").replace(/^[\s\S]*?\/mo\s*/, "");
};

export default ShopPayInstallments;
